import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, UserPlus, Trash, PencilSimple, Users, Crown, ShieldCheck, User as UserIcon } from "@phosphor-icons/react";
import InviteManager from "@/components/InviteManager";

const ROLE_META = {
  owner: { label: "Owner", Icon: Crown, bg: "#FEF9C3" },
  manager: { label: "Manager", Icon: ShieldCheck, bg: "#E0F2FE" },
  employee: { label: "Employee", Icon: UserIcon, bg: "#F3F4F6" },
};

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";

const empty = { name: "", email: "", password: "", role: "employee", phone: "" };

export default function TeamPanel({ open, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("members");

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => { if (open) { load(); setForm(empty); setEditingId(null); setTab("members"); } }, [open, load]);
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const roleOptions = isOwner ? ["employee", "manager", "owner"] : ["employee"];

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    if (!editingId && !form.password.trim()) { toast.error("Password required"); return; }
    setBusy(true);
    try {
      if (editingId) {
        const payload = { name: form.name, email: form.email, role: form.role, phone: form.phone || "" };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/users", form);
        toast.success("Member added");
      }
      setForm(empty); setEditingId(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const edit = (u) => { setEditingId(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); };
  const cancel = () => { setEditingId(null); setForm(empty); };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? Their assigned tasks will become unassigned.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Member removed"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="team-panel" onClick={(e) => e.stopPropagation()} className="relative bg-white border-l-2 border-black w-full max-w-md h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#E0F2FE] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><Users size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">Team</h2>
          </div>
          <button data-testid="close-team-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black bg-white flex items-center justify-center"><X size={18} weight="bold" /></button>
        </div>

        {isOwner && (
          <div className="sticky top-[65px] bg-white z-10 px-5 pt-4 pb-3 flex gap-2 border-b-2 border-black">
            <button data-testid="team-tab-members" onClick={() => setTab("members")} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black ${tab === "members" ? "bg-secondary text-white" : "bg-white"}`}>Members</button>
            <button data-testid="team-tab-invite" onClick={() => setTab("invite")} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black ${tab === "invite" ? "bg-secondary text-white" : "bg-white"}`}>Invite</button>
          </div>
        )}

        {isOwner && tab === "invite" ? (
          <div className="p-5"><InviteManager /></div>
        ) : (
        <div className="p-5">
          {/* Add / edit form */}
          <div className="bg-[#F9F9F6] border-2 border-black rounded-xl p-4 mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mb-3">{editingId ? "Edit member" : "Add member"}</p>
            <div className="space-y-3">
              <input data-testid="team-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <input data-testid="team-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              <input data-testid="team-password-input" type="password" className={inputCls} placeholder={editingId ? "New password (optional)" : "Password"} value={form.password} onChange={(e) => set("password", e.target.value)} />
              <input data-testid="team-phone-input" type="tel" inputMode="tel" className={inputCls} placeholder="Mobile number for text alerts (e.g. +1 808 555 1234)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <select data-testid="team-role-select" className={inputCls} value={form.role} onChange={(e) => set("role", e.target.value)}>
                {roleOptions.map((r) => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              {editingId && <button data-testid="team-cancel-btn" onClick={cancel} className="press flex-1 h-11 rounded-full bg-white font-bold text-sm border-2 border-black">Cancel</button>}
              <button data-testid="team-save-btn" onClick={submit} disabled={busy} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold text-sm border-2 border-black flex items-center justify-center gap-2 disabled:opacity-60">
                <UserPlus size={16} weight="bold" /> {busy ? "Saving…" : editingId ? "Update" : "Add Member"}
              </button>
            </div>
          </div>

          {/* Member list */}
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mb-3">Members ({users.length})</p>
          <div className="space-y-2">
            {users.map((u) => {
              const rm = ROLE_META[u.role] || ROLE_META.employee;
              const canManage = isOwner || !["owner", "store_manager"].includes(u.role);
              return (
                <div key={u.id} data-testid={`team-member-${u.id}`} className="flex items-center gap-3 border-2 border-black rounded-lg p-3 bg-white">
                  <div className="w-10 h-10 shrink-0 rounded-full border-2 border-black flex items-center justify-center font-bold" style={{ backgroundColor: rm.bg }}>{u.name?.[0]}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm leading-tight truncate">{u.name}{u.id === user.id && <span className="text-black/40 font-normal"> (you)</span>}</p>
                    <p className="text-xs text-black/50 truncate">{u.email}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border-2 border-black shrink-0" style={{ backgroundColor: rm.bg }}><rm.Icon size={11} weight="bold" /> {rm.label}</span>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      <button data-testid={`edit-member-${u.id}`} onClick={() => edit(u)} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center"><PencilSimple size={13} weight="bold" /></button>
                      {u.id !== user.id && <button data-testid={`delete-member-${u.id}`} onClick={() => remove(u)} className="press w-8 h-8 rounded-full border-2 border-black bg-primary text-white flex items-center justify-center"><Trash size={13} weight="bold" /></button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
